import React, { useState, useEffect } from 'react';
import StudentModal from '../components/StudentManagement/StudentModal';
import KyTestModal from '../components/StudentManagement/KyTestModal';
import MemberStatusModal from '../components/StudentManagement/MemberStatusModal';
import { AITrackerContent } from '../components/AITrackerContent';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { PenSquare, UserPlus, Mail, School, User, BookOpen, MoreVertical, Phone, ChevronLeft, ChevronRight, TrendingUp, Bot, Cpu, ClipboardList, Users } from 'lucide-react';
import { Button, Modal, ConfirmModal, BypassLayout, DesktopPagination } from '../components/common';
import { useDesktopExpanded } from '../hooks/useDesktopExpanded';
import { ACADEMY_CUTOVER } from '../utils/roles';
import '../styles/desktop-expanded.css';

function StudentManagement() {
  const { page_number } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken, userId, userType } = useAuth();
  const [page, setPage] = useState(parseInt(page_number) || 1);
  const [pageInputValue, setPageInputValue] = useState(String(parseInt(page_number) || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [isTeachersLoading, setIsTeachersLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [allTeachers, setAllTeachers] = useState([]); // 강사 배정용 (필터링 없음)
  
  // 데스크톱 확장 훅
  const { isMobileView, shouldUsePagination } = useDesktopExpanded();
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    student_name: searchParams.get('student_name') || '',
    in_charge_teacher: searchParams.get('in_charge_teacher') || '',
    lecture: searchParams.get('lecture') || '',
    school_name: searchParams.get('school_name') || ''
  });
  const [selectedSchoolType, setSelectedSchoolType] = useState(null);
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [isFilterMode, setIsFilterMode] = useState(
    searchParams.get('student_name') || 
    searchParams.get('in_charge_teacher') || 
    searchParams.get('lecture')
  );
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showMemberStatus, setShowMemberStatus] = useState(false);
  const [showKyTestModal, setShowKyTestModal] = useState(false);
  const [selectedKyTestStudent, setSelectedKyTestStudent] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedAIStudent, setSelectedAIStudent] = useState(null);
  const [aiTrackerTab, setAiTrackerTab] = useState('ai'); // 'ai' | 'engine' | 'rawdata'
  const tableContainerRef = React.useRef(null);

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = () => {
    try {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const buffer = 10; // 여유분

      let availableHeight, rowHeight;

      if (isMobileView) {
        // ============ 모바일 ============
        // BypassLayout 제공 영역: windowHeight - Header(50) - MobileNav(68)
        const bypassLayoutHeight = windowHeight - 50 - 68;
        // 카드 영역: BypassLayout - margin(29)
        const cardHeight = bypassLayoutHeight - 29;
        // 카드 내부 고정 요소:
        // - 카드헤더(154): title(36) + mt-3(12) + filterGrid(56) + mt-3(12) + schoolBtns(26) + marginBottom(12)
        // - contentMarginTop(12)
        // - 테이블헤더(35)
        // - 페이지네이션(40)
        const cardInternalFixed = 154 + 12 + 35 + 40 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 48; // py-2(16px) + avatar h-6(24px) + 텍스트 = ~46px
      } else {
        // ============ 데스크톱 ============
        // 카드 높이: calc(100vh - 6rem) = windowHeight - 96px
        const cardHeight = windowHeight - 96;
        // 카드 내부 고정 요소:
        // - padding(32) + 타이틀(28) + 필터(80) + marginTop(16) + 테이블헤더(40) + 페이지네이션(60)
        const cardInternalFixed = 32 + 28 + 80 + 16 + 40 + 60 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 48; // py-2(16px) + avatar h-8(32px) = ~48px
      }

      const itemsCount = Math.floor(availableHeight / rowHeight);

      // 최소/최대 개수 제한
      const minItems = 5;
      const maxItems = isMobileView ? 30 : 50;

      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 10; // 기본값
    }
  };

  const fetchStudents = async (pageNum = 1, useFilter = false) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      let url;
      const itemsPerPage = calculateItemsPerPage();
      const params = new URLSearchParams({
        page: pageNum,
        per_page: itemsPerPage
      });

      if (useFilter) {
        if (filters.student_name) params.append('student_name', filters.student_name);
        if (filters.in_charge_teacher) params.append('in_charge_teacher', filters.in_charge_teacher);
        if (filters.lecture) params.append('lecture', filters.lecture);
        if (filters.school_name) params.append('school_name', filters.school_name);
      }

      if (userType === 'manager') {
        url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
      } else if (userType === 'teacher') {
        params.append('teacher_id', userId);
        url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (response.status === 404) {
        setStudents([]);
        setTotalPages(1);
        setHasMore(false);
        setPage(1);
        return;
      }

      if (!response.ok) {
        throw new Error('학생 목록을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setStudents(result.data);
      setTotalPages(result.total_pages || 1);
      setHasMore(pageNum < result.total_pages);
      setPage(pageNum);
      setTotalItems(result.total_items || result.total_count || 0);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
      setTotalPages(1);
      setHasMore(false);
      setPage(1);
    } finally {
      setIsLoading(false);
    }
  };

  const updateURL = (pageNum, filterData) => {
    const params = new URLSearchParams();
    
    if (filterData.student_name) params.append('student_name', filterData.student_name);
    if (filterData.in_charge_teacher) params.append('in_charge_teacher', filterData.in_charge_teacher);
    if (filterData.lecture) params.append('lecture', filterData.lecture);
    if (filterData.school_name) params.append('school_name', filterData.school_name);
    
    const queryString = params.toString();
    navigate(`/student/${pageNum}${queryString ? `?${queryString}` : ''}`);
  };

  const handleSearch = () => {
    setIsFilterMode(true);
    updateURL(1, filters);
    fetchStudents(1, true);
  };

  const handleReset = () => {
    setIsFilterMode(false);
    setFilters({
      student_name: '',
      in_charge_teacher: '',
      lecture: '',
      school_name: ''
    });
    setSelectedSchoolType(null);
    navigate('/student/1');
    fetchStudents(1, false);
  };

  const handlePageChange = (newPage) => {
    updateURL(newPage, filters);
    fetchStudents(newPage, isFilterMode);
  };

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin', { replace: true });
        return;
      }
      const pageNum = parseInt(page_number) || 1;
      setPage(pageNum);
      setPageInputValue(String(pageNum));
      
      const hasFilters = searchParams.toString().length > 0;
      setIsFilterMode(hasFilters);
      
      setFilters({
        student_name: searchParams.get('student_name') || '',
        in_charge_teacher: searchParams.get('in_charge_teacher') || '',
        lecture: searchParams.get('lecture') || '',
        school_name: searchParams.get('school_name') || ''
      });
      
      fetchStudents(pageNum, hasFilters);
    };

    checkAuthAndFetch();
  }, [page_number, location.search]);

  // 화면 크기 변경 시 per_page 재계산
  useEffect(() => {
    const handleResize = () => {
      // 화면 크기가 변경되면 데이터를 다시 불러옴
      if (students.length > 0) {
        fetchStudents(page, isFilterMode);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [page, isFilterMode, isMobileView]);

  // 컴포넌트 마운트 시 선생님 목록 가져오기
  useEffect(() => {
    if (userType === 'manager') {
      fetchTeachers();
    }
  }, [userType]);

  // 학교 이름 필터가 변경되면 핫키 상태도 업데이트
  useEffect(() => {
    if (!filters.school_name) {
      setSelectedSchoolType(null);
    } else {
      const reverseHotkeyMap = {
        '초등학교': 'elementary_school',
        '중학교': 'middle_school',
        '고등학교': 'high_school'
      };
      const matchedHotkey = reverseHotkeyMap[filters.school_name];
      if (matchedHotkey !== selectedSchoolType) {
        setSelectedSchoolType(matchedHotkey || null);
      }
    }
  }, [filters.school_name, selectedSchoolType]);

  // 핫키로 필터 변경 시 자동 검색
  useEffect(() => {
    if (shouldAutoSearch) {
      setShouldAutoSearch(false);
      handleSearch();
    }
  }, [filters.school_name, shouldAutoSearch]);

  const handleAddStudent = async (studentData) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/student`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(studentData)
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '학생 추가에 실패했습니다.');
      }

      const result = await response.json();
      if (result.status === 'success') {
        await fetchStudents();
        return '학생이 성공적으로 추가되었습니다.';
      }
    } catch (error) {
      throw error;
    }
  };

  const handleEditStudent = async (studentData) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/student`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: selectedStudent.student_id,
          ...studentData
        })
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '학생 정보 수정에 실패했습니다.');
      }

      const result = await response.json();
      if (result.status === 'success') {
        await fetchStudents(page, isFilterMode);
        setSelectedStudentDetail({
          ...selectedStudentDetail,
          ...studentData
        });
        setIsModalOpen(false);
        setShowDetailModal(true);
        return '학생 정보가 성공적으로 수정되었습니다.';
      }
    } catch (error) {
      throw error;
    }
  };

  const handleModalOpen = (mode, student = null, fieldName = null) => {
    setModalMode(mode);
    const studentData = {
      ...student,
      editField: fieldName,
      email: student?.student_email,
      phone_number: student?.mobile_number,
      parent_phone_number: student?.parent_mobile_number
    };
    setSelectedStudent(studentData);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
    setModalMode('add');
    fetchStudents(page, isFilterMode);
    
    if (modalMode === 'edit') {
      setShowDetailModal(true);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(start + maxVisible - 1, totalPages);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (isMobileView) {
      // 모바일 뷰 - 기존 Button 컴포넌트 유지
      if (start > 1) {
        pageNumbers.push(
          <button
            key={1}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        );
        if (start > 2) {
          pageNumbers.push(<span key="ellipsis1" className="px-1 text-gray-400">...</span>);
        }
      }

      for (let i = start; i <= end; i++) {
        if (page === i) {
          // 현재 페이지 - 인풋 박스로 렌더링
          const inputWidth = Math.max(2, String(pageInputValue).length * 0.6 + 1.2);
          pageNumbers.push(
            <input
              key={i}
              type="text"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setPageInputValue(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newPage = parseInt(pageInputValue);
                  if (newPage >= 1 && newPage <= totalPages) {
                    e.target.blur(); // 키보드 내리기
                    // 키보드가 내려간 후 페이지 이동 (화면 높이 복원 대기)
                    setTimeout(() => {
                      handlePageChange(newPage);
                    }, 100);
                  } else {
                    setPageInputValue(String(page));
                  }
                }
              }}
              onMouseUp={(e) => e.preventDefault()}
              onFocus={(e) => e.target.select()}
              onBlur={() => setPageInputValue(String(page))}
              className="flex items-center justify-center rounded-full border border-primary bg-primary text-white text-xs font-bold"
              style={{ width: `${inputWidth}rem`, minWidth: '2rem', height: '2rem', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 4px' }}
            />
          );
        } else {
          pageNumbers.push(
            <button
              key={i}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
              onClick={() => handlePageChange(i)}
            >
              {i}
            </button>
          );
        }
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pageNumbers.push(<span key="ellipsis2" className="px-1 text-gray-400">...</span>);
        }
        pageNumbers.push(
          <button
            key={totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }
    } else {
      // 데스크톱 뷰 - 시험분석 페이지 스타일
      if (start > 1) {
        pageNumbers.push(
          <button
            key={1}
            className="page-button"
            style={{ borderRadius: '1.5rem' }}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        );
        if (start > 2) {
          pageNumbers.push(<span key="ellipsis1" className="page-dots">...</span>);
        }
      }

      for (let i = start; i <= end; i++) {
        if (page === i) {
          // 현재 페이지 - 인풋 박스로 렌더링
          const inputWidth = Math.max(2.5, String(pageInputValue).length * 0.6 + 1.5);
          pageNumbers.push(
            <input
              key={i}
              type="text"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setPageInputValue(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newPage = parseInt(pageInputValue);
                  if (newPage >= 1 && newPage <= totalPages) {
                    e.target.blur(); // 키보드 내리기
                    // 키보드가 내려간 후 페이지 이동 (화면 높이 복원 대기)
                    setTimeout(() => {
                      handlePageChange(newPage);
                    }, 100);
                  } else {
                    setPageInputValue(String(page));
                  }
                }
              }}
              onMouseUp={(e) => e.preventDefault()}
              onFocus={(e) => e.target.select()}
              onBlur={() => setPageInputValue(String(page))}
              className="page-button active font-bold"
              style={{
                borderRadius: '1.5rem',
                width: `${inputWidth}rem`,
                minWidth: '2.5rem',
                height: '40px',
                textAlign: 'center',
                outline: 'none',
                border: 'none',
                cursor: 'text',
                padding: '0 8px'
              }}
              aria-label="페이지 번호 입력"
            />
          );
        } else {
          pageNumbers.push(
            <button
              key={i}
              className="page-button"
              style={{ borderRadius: '1.5rem' }}
              onClick={() => handlePageChange(i)}
              aria-label={`${i} 페이지`}
            >
              {i}
            </button>
          );
        }
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pageNumbers.push(<span key="ellipsis2" className="page-dots">...</span>);
        }
        pageNumbers.push(
          <button
            key={totalPages}
            className="page-button"
            style={{ borderRadius: '1.5rem' }}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }
    }

    return pageNumbers;
  };

  const fetchTeachers = async () => {
    try {
      setIsTeachersLoading(true);
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/teacher?page=1&per_page=50`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) throw new Error('강사 목록을 불러오는데 실패했습니다.');
      
      const result = await response.json();
      // 담당업무가 "원장", "강사", "팀장"인 경우만 필터링
      const filteredTeachers = result.data?.filter(teacher => 
        ['원장', '강사', '팀장'].includes(teacher.charge_work)
      ) || [];
      setTeachers(filteredTeachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setIsTeachersLoading(false);
    }
  };

  // 강사 배정용 - 모든 선생님 가져오기 (필터링 없음)
  const fetchAllTeachers = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/teacherstotal`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('강사 목록을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setAllTeachers(result.data || []);
    } catch (error) {
      console.error('Error fetching all teachers:', error);
      setAllTeachers([]);
    }
  };

  const handleAllocateTeacher = async (studentId, teacherId) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            teacher_id: teacherId,
            student_id: studentId
          })
        }
      );

      if (!response.ok) throw new Error('강사 할당에 실패했습니다.');

      await fetchStudents(page, isFilterMode);
      setShowTeacherModal(false);
      setSelectedTeacherId('');
      setShowDetailModal(true);
      
      const message = teacherId ? '강사가 성공적으로 할당되었습니다.' : '강사 할당이 삭제되었습니다.';
      alert(message);
    } catch (error) {
      console.error('Error allocating teacher:', error);
      alert(error.message);
    }
  };

  const handleTeacherModalClose = () => {
    setShowTeacherModal(false);
    setSelectedTeacherId('');
    setShowDetailModal(true);
    fetchStudents(page, isFilterMode);
  };

  const handleRowClick = (student) => {
    setSelectedStudentDetail(student);
    setShowDetailModal(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResetPassword = async (studentId) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/resetpw`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ student_id: parseInt(studentId) })
      });

      if (!response.ok) {
        throw new Error('비밀번호 초기화에 실패했습니다.');
      }

      const result = await response.json();
      if (result.status === 'success') {
        return result.data.temp_pw;
      } else {
        throw new Error(result.message || '비밀번호 초기화에 실패했습니다.');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSchoolTypeFilter = (schoolType, forceSearch = true) => {
    setSelectedSchoolType(schoolType);
    
    if (schoolType) {
      const hotkeyMap = {
        'elementary_school': '초등학교',
        'middle_school': '중학교',
        'high_school': '고등학교'
      };
      
      // 학교 이름 필터에 핫키 값 설정하고 자동 검색 플래그 설정
      setShouldAutoSearch(true);
      setFilters(prev => ({ ...prev, school_name: hotkeyMap[schoolType] }));
    } else {
      // 핫키 비활성화 시 학교 이름 필터 클리어
      if (forceSearch) {
        setShouldAutoSearch(true);
      }
      setFilters(prev => ({ ...prev, school_name: '' }));
    }
  };

  const handleDelete = async () => {
    if (deleteEmail !== selectedStudentDetail.student_email) {
      setDeleteError('이메일이 일치하지 않습니다.');
      return;
    }

    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/student`, {
        method: 'DELETE',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: parseInt(selectedStudentDetail.student_id)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '학생 삭제에 실패했습니다.');
      }

      alert('학생이 성공적으로 삭제되었습니다.');
      setShowDetailModal(false);
      setShowDeleteConfirm(false);
      setDeleteEmail('');
      fetchStudents(page, isFilterMode);
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  return (
    <BypassLayout
      loading={isLoading && students.length === 0}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <>
          {/* 메인 카드 컨테이너 */}
          <div 
            className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
              isMobileView ? 'flex-1' : 'h-[calc(100vh-6rem)] w-full'
            }`}
            style={{
              ...getSafeAreaStyle(),
              width: isMobileView ? 'calc(100% - 28.8px)' : '100%',
              height: isMobileView ? 'calc(100% - 28.8px)' : '100%',
              margin: isMobileView ? '14.4px' : '0'
            }}
          >
            {/* 헤더 영역 */}
            <div className="shrink-0 border-b">
              <div style={{ 
                padding: isMobileView ? '0' : '16px',
                marginBottom: isMobileView ? '12px' : '0'
              }}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">학생 관리</h3>
                    <p className="text-xs text-gray-500">
                      - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>명 중 <span className="font-semibold text-primary">{students?.length || 0}</span>명 표시 중
                    </p>
                  </div>
                  {/* 우측 상단 액션 버튼 */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      onClick={() => setShowMemberStatus(true)}
                      className="text-xs rounded-3xl !bg-white !text-primary !border !border-primary hover:!bg-primary/10"
                    >
                      <Users size={14} className="mr-1" />
                      회원 현황
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => navigate('/student/exam-list')}
                      className="text-xs rounded-3xl !bg-white !text-primary !border !border-primary hover:!bg-primary/10"
                    >
                      <ClipboardList size={14} className="mr-1" />
                      전체 테스트
                    </Button>
                  </div>
                </div>
                
                {/* 필터 및 액션 버튼 */}
                <div className="space-y-4">
                  {/* 필터 입력 필드들 */}
                  {isMobileView ? (
                    <div>
                      <div className="mt-3 flex gap-2">
                        <div className="flex-1 grid gap-2 grid-cols-2">
                          <input
                            type="text"
                            name="student_name"
                            placeholder="학생 이름"
                            value={filters.student_name}
                            onChange={handleFilterChange}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1 text-xs"
                          />
                          {userType !== 'teacher' ? (
                            <select
                              name="in_charge_teacher"
                              value={filters.in_charge_teacher}
                              onChange={handleFilterChange}
                              className="w-full rounded border px-2 py-1 text-xs bg-white"
                              disabled={isTeachersLoading}
                            >
                              <option value="">
                                {isTeachersLoading ? '강사 목록 로딩중...' : '담당 강사'}
                              </option>
                              {teachers.map((teacher) => (
                                <option key={teacher.user_id} value={teacher.user_name}>
                                  {teacher.user_name} ({teacher.charge_work})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              name="in_charge_teacher"
                              placeholder="담당 강사"
                              value={filters.in_charge_teacher}
                              onChange={handleFilterChange}
                              onKeyDown={handleKeyPress}
                              className="w-full rounded border px-2 py-1 text-xs"
                              disabled
                            />
                          )}
                          <input
                            type="text"
                            name="school_name"
                            placeholder="학교 이름"
                            value={filters.school_name || ''}
                            onChange={handleFilterChange}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1 text-xs"
                          />
                          <input
                            type="text"
                            name="lecture"
                            placeholder="강의"
                            value={filters.lecture}
                            onChange={handleFilterChange}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1 text-xs"
                          />
                        </div>
                        <div className="flex w-16 flex-col gap-2">
                          <Button
                            onClick={handleReset}
                            variant="secondary"
                            size="sm"
                            rounded="3xl"
                            className="flex-1 min-h-[22px]"
                          >
                            초기화
                          </Button>
                          <Button
                            onClick={handleSearch}
                            variant="primary"
                            size="sm"
                            rounded="3xl"
                            className="flex-1 min-h-[22px]"
                          >
                            조회
                          </Button>
                        </div>
                      </div>
                      
                      {/* 검색 핫키 버튼들 */}
                      <div className="mt-3">
                        <div className="flex gap-2 overflow-x-auto">
                          <button
                            onClick={() => {
                              if (selectedSchoolType === 'elementary_school') {
                                handleSchoolTypeFilter(null, true);
                              } else {
                                handleSchoolTypeFilter('elementary_school', true);
                              }
                            }}
                            className={`
                              flex-1 px-4 py-1.5 text-sm transition-all duration-200
                              ${selectedSchoolType === 'elementary_school'
                                ? 'bg-primary text-white font-bold'
                                : 'bg-gray-100 text-gray-700 font-medium'
                              }
                            `}
                            style={{ borderRadius: '20px' }}
                          >
                            초등학교
                          </button>
                          <button
                            onClick={() => {
                              if (selectedSchoolType === 'middle_school') {
                                handleSchoolTypeFilter(null, true);
                              } else {
                                handleSchoolTypeFilter('middle_school', true);
                              }
                            }}
                            className={`
                              flex-1 px-4 py-1.5 text-sm transition-all duration-200
                              ${selectedSchoolType === 'middle_school'
                                ? 'bg-primary text-white font-bold'
                                : 'bg-gray-100 text-gray-700 font-medium'
                              }
                            `}
                            style={{ borderRadius: '20px' }}
                          >
                            중학교
                          </button>
                          <button
                            onClick={() => {
                              if (selectedSchoolType === 'high_school') {
                                handleSchoolTypeFilter(null, true);
                              } else {
                                handleSchoolTypeFilter('high_school', true);
                              }
                            }}
                            className={`
                              flex-1 px-4 py-1.5 text-sm transition-all duration-200
                              ${selectedSchoolType === 'high_school'
                                ? 'bg-primary text-white font-bold'
                                : 'bg-gray-100 text-gray-700 font-medium'
                              }
                            `}
                            style={{ borderRadius: '20px' }}
                          >
                            고등학교
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mt-3 flex gap-2">
                        <div className="flex-1 flex gap-2">
                          <input
                            type="text"
                            name="student_name"
                            placeholder="학생 이름"
                            value={filters.student_name}
                            onChange={handleFilterChange}
                            onKeyDown={handleKeyPress}
                            className="flex-1 rounded border px-2 py-1 text-xs"
                          />
                          <input
                            type="text"
                            name="school_name"
                            placeholder="학교 이름"
                            value={filters.school_name || ''}
                            onChange={handleFilterChange}
                            onKeyDown={handleKeyPress}
                            className="flex-1 rounded border px-2 py-1 text-xs"
                          />
                          {userType !== 'teacher' ? (
                            <select
                              name="in_charge_teacher"
                              value={filters.in_charge_teacher}
                              onChange={handleFilterChange}
                              className="flex-1 rounded border px-2 py-1 text-xs bg-white"
                              disabled={isTeachersLoading}
                            >
                              <option value="">
                                {isTeachersLoading ? '강사 목록 로딩중...' : '담당 강사'}
                              </option>
                              {teachers.map((teacher) => (
                                <option key={teacher.user_id} value={teacher.user_name}>
                                  {teacher.user_name} ({teacher.charge_work})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              name="in_charge_teacher"
                              placeholder="담당 강사"
                              value={filters.in_charge_teacher}
                              onChange={handleFilterChange}
                              onKeyDown={handleKeyPress}
                              className="flex-1 rounded border px-2 py-1 text-xs"
                              disabled
                            />
                          )}
                          <input
                            type="text"
                            name="lecture"
                            placeholder="강의"
                            value={filters.lecture}
                            onChange={handleFilterChange}
                            onKeyDown={handleKeyPress}
                            className="flex-1 rounded border px-2 py-1 text-xs"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleReset}
                            variant="secondary"
                            size="sm"
                            rounded="3xl"
                            className="px-6 min-h-[22px]"
                          >
                            초기화
                          </Button>
                          <Button
                            onClick={handleSearch}
                            variant="primary"
                            size="sm"
                            rounded="3xl"
                            className="px-6 min-h-[22px]"
                          >
                            조회
                          </Button>
                        </div>
                      </div>
                      
                      {/* 검색 핫키 버튼들 - 데스크톱에서는 아래 라인에 배치 */}
                      <div className="mt-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (selectedSchoolType === 'elementary_school') {
                                handleSchoolTypeFilter(null, true);
                              } else {
                                handleSchoolTypeFilter('elementary_school', true);
                              }
                            }}
                            className={`
                              flex-1 px-4 py-1.5 text-sm transition-all duration-200
                              ${selectedSchoolType === 'elementary_school'
                                ? 'bg-primary text-white font-bold'
                                : 'bg-gray-100 text-gray-700 font-medium'
                              }
                            `}
                            style={{ borderRadius: '20px' }}
                          >
                            초등학교
                          </button>
                          <button
                            onClick={() => {
                              if (selectedSchoolType === 'middle_school') {
                                handleSchoolTypeFilter(null, true);
                              } else {
                                handleSchoolTypeFilter('middle_school', true);
                              }
                            }}
                            className={`
                              flex-1 px-4 py-1.5 text-sm transition-all duration-200
                              ${selectedSchoolType === 'middle_school'
                                ? 'bg-primary text-white font-bold'
                                : 'bg-gray-100 text-gray-700 font-medium'
                              }
                            `}
                            style={{ borderRadius: '20px' }}
                          >
                            중학교
                          </button>
                          <button
                            onClick={() => {
                              if (selectedSchoolType === 'high_school') {
                                handleSchoolTypeFilter(null, true);
                              } else {
                                handleSchoolTypeFilter('high_school', true);
                              }
                            }}
                            className={`
                              flex-1 px-4 py-1.5 text-sm transition-all duration-200
                              ${selectedSchoolType === 'high_school'
                                ? 'bg-primary text-white font-bold'
                                : 'bg-gray-100 text-gray-700 font-medium'
                              }
                            `}
                            style={{ borderRadius: '20px' }}
                          >
                            고등학교
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

                         {/* 컨텐츠 영역 */}
             <div className={`${isMobileView ? 'flex-1 overflow-y-auto' : 'flex-1 overflow-y-auto'}`} style={{ 
               padding: '0',
               marginTop: isMobileView ? '12px' : '16px',
               maxHeight: isMobileView ? 'calc(100vh - 10rem)' : 'calc(100vh - 14rem)',
               display: 'flex',
               flexDirection: 'column',
               height: '100%'
             }}>
               {isLoading ? (
                 <div className="py-4 text-center">로딩 중...</div>
               ) : (
                 <div ref={tableContainerRef} className="overflow-x-auto flex-1" style={{ minHeight: 0, width: '100%' }}>
                     <table className="w-full">
                       <thead className="bg-gray-50">
                         <tr>
                           <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                             학생/이메일
                           </th>
                           <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                             학교/학년
                           </th>
                           <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                             담당/강의
                           </th>
                           {!isMobileView && (
                             <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                               연락처/학부모
                             </th>
                           )}
                           <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                             관리
                           </th>
                         </tr>
                       </thead>
                       <tbody className="bg-white">
                         {students.map((student, index) => (
                           <tr 
                             key={student.student_id}
                             onClick={() => handleRowClick(student)}
                             className={`hover:bg-primary/10 cursor-pointer transition-colors duration-150 border-b border-gray-100 ${
                               student.is_blocked ? 'bg-red-50' : ''
                             }`}
                           >
                             <td className={`${isMobileView ? 'px-1 max-w-[90px] rounded-l-xl' : 'px-3 rounded-l-3xl'} py-2 whitespace-nowrap transition-colors duration-150`}>
                               <div className="flex items-center">
                                 <div className={`flex-shrink-0 ${isMobileView ? 'h-6 w-6' : 'h-8 w-8'}`}>
                                   <div className={`${isMobileView ? 'h-6 w-6 text-xs' : 'h-8 w-8 text-sm'} rounded-full flex items-center justify-center text-white font-bold ${
                                     student.is_blocked
                                       ? 'bg-gradient-to-r from-red-500 to-red-600'
                                       : 'bg-gradient-to-r from-primary to-primary/70'
                                   }`}>
                                     {student.student_id}
                                   </div>
                                 </div>
                                 <div className={`${isMobileView ? 'ml-1 min-w-0 overflow-hidden' : 'ml-3'}`}>
                                   {isMobileView ? (
                                     <>
                                       <div className="text-xs font-medium text-gray-900 whitespace-nowrap leading-tight">
                                         {student.student_name}
                                       </div>
                                       <div className="text-xs text-gray-500 truncate leading-tight max-w-[55px]">
                                         {student.student_email}
                                       </div>
                                     </>
                                   ) : (
                                     <div className="flex items-center gap-3">
                                       <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                                         {student.student_name}
                                       </div>
                                       <div className="text-xs text-gray-500">
                                         {student.student_email}
                                       </div>
                                     </div>
                                   )}
                                 </div>
                               </div>
                             </td>
                             <td className={`${isMobileView ? 'px-1 max-w-[70px]' : 'px-3'} py-2 whitespace-nowrap transition-colors duration-150`}>
                               <div className="text-sm">
                                 {isMobileView ? (
                                   <>
                                     <div className="text-xs text-gray-900 truncate leading-tight max-w-[60px]">{student.school_name}</div>
                                     <div className="text-xs text-gray-500 whitespace-nowrap leading-tight">{student.school_grade}학년</div>
                                   </>
                                 ) : (
                                   <div className="flex items-center gap-2">
                                     <div className="text-sm text-gray-900 whitespace-nowrap">{student.school_name}</div>
                                     <div className="text-xs text-gray-500">({student.school_grade}학년)</div>
                                   </div>
                                 )}
                               </div>
                             </td>
                                                         <td className={`${isMobileView ? 'px-1 max-w-[60px]' : 'px-3'} py-2 whitespace-nowrap transition-colors duration-150`}>
                              <div className="text-sm">
                                {isMobileView ? (
                                  <>
                                    <div className="text-xs text-gray-900 truncate leading-tight max-w-[50px]">{student.in_charge_teacher || '-'}</div>
                                    <div className="text-xs text-gray-500 truncate leading-tight max-w-[50px]">{student.lecture || '-'}</div>
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm text-gray-900 whitespace-nowrap">{student.in_charge_teacher || '-'}</div>
                                    {student.lecture && (
                                      <div className="text-xs text-gray-500">({student.lecture})</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            {!isMobileView && (
                              <td className="px-3 py-2 whitespace-nowrap transition-colors duration-150">
                                <div className="text-sm">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1 text-sm text-gray-900 whitespace-nowrap">
                                      <Phone className="w-3 h-3 text-gray-400" />
                                      <span>{student.mobile_number}</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      (학부모: {student.parent_mobile_number})
                                    </div>
                                  </div>
                                </div>
                              </td>
                            )}
                             <td className={`${isMobileView ? 'px-1 rounded-r-xl' : 'px-3 rounded-r-3xl'} py-1 whitespace-nowrap text-right text-sm font-medium transition-colors duration-150`}>
                               <div className={`flex items-center justify-end ${isMobileView ? 'gap-0.5' : 'gap-1'}`}>
                                 <Button
                                   variant="primary"
                                   size="sm"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     navigate(`/student/${student.student_id}/kytest`);
                                   }}
                                   rounded="3xl"
                                   className={`text-xs ${isMobileView ? 'h-[30px] inline-flex items-center justify-center py-0 px-1.5' : 'py-1 px-3'}`}
                                 >
                                   {isMobileView ? 'Ky' : 'Ky Test'}
                                 </Button>
                                 <Button
                                   variant="ghost"
                                   size="xs"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     navigate(`/learning-status/student/${student.student_id}`);
                                   }}
                                  className={`flex items-center justify-center rounded-3xl p-0 text-primary hover:text-primary-hover ${isMobileView ? 'size-[30px]' : 'size-8'}`}
                                   title="학습분석"
                                 >
                                   <TrendingUp size={isMobileView ? 12 : 16} strokeWidth={2.5} />
                                 </Button>
                                 {/* <Button
                                   variant="ghost"
                                   size="xs"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     setSelectedAIStudent(student);
                                     setShowAIModal(true);
                                   }}
                                  className={`flex items-center justify-center rounded-3xl p-0 text-primary hover:text-primary-hover ${isMobileView ? 'size-[30px]' : 'size-8'}`}
                                   title="AI"
                                 >
                                   <Bot size={isMobileView ? 12 : 16} strokeWidth={2.5} />
                                 </Button> */}
                               </div>
                             </td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                 </div>
               )}

               {/* 페이지네이션 - 모바일/데스크톱 분기 */}
               {!isLoading && students.length > 0 && (
                 <div className="shrink-0 bg-white">
                   <div className={isMobileView ? "flex justify-center items-center gap-2" : "desktop-pagination"}>
                     {/* 이전 버튼 */}
                     <button 
                       className={isMobileView ? "w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" : "page-button"}
                       style={!isMobileView ? { borderRadius: '1.5rem' } : {}}
                       onClick={() => handlePageChange(page - 1)}
                       disabled={page === 1}
                       aria-label="이전 페이지"
                     >
                       {isMobileView ? '<' : <ChevronLeft size={16} className="font-bold" />}
                     </button>
                     
                     {/* 페이지 번호들 */}
                     <div className="flex gap-1">
                       {renderPageNumbers()}
                     </div>
                     
                     {/* 다음 버튼 */}
                     <button 
                       className={isMobileView ? "w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" : "page-button"}
                       style={!isMobileView ? { borderRadius: '1.5rem' } : {}}
                       onClick={() => handlePageChange(page + 1)}
                       disabled={!hasMore}
                       aria-label="다음 페이지"
                     >
                       {isMobileView ? '>' : <ChevronRight size={16} className="font-bold" />}
                     </button>
                   </div>
                 </div>
               )}

               {!isLoading && students.length === 0 && (
                 <div className="py-8 text-center text-gray-500">
                   학생 데이터가 없습니다.
                 </div>
               )}
             </div>
           </div>

          {/* 모든 모달들을 BypassLayout 내부로 이동 */}
          
          <StudentModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={modalMode === 'add' ? handleAddStudent : handleEditStudent}
        initialData={selectedStudent}
        mode={modalMode}
        selectedStudentDetail={selectedStudentDetail}
      />

      <Modal
        isOpen={showDetailModal && selectedStudentDetail}
        onClose={() => setShowDetailModal(false)}
        title={`${selectedStudentDetail?.student_name || ''} 학생 상세 정보`}
        size="md"
        footer={
          <div className="flex w-full justify-between">
            <div className="flex gap-2">
              {userType === 'manager' && (
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  삭제
                </Button>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={() => setShowDetailModal(false)}
            >
              닫기
            </Button>
          </div>
        }
      >
        {selectedStudentDetail && (
          <>
            <div className="mb-4 flex justify-end gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowResetConfirm(true)}
              >
                비밀번호 리셋
              </Button>
              {userType === 'manager' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedStudent(selectedStudentDetail);
                    setShowTeacherModal(true);
                    fetchAllTeachers();
                  }}
                >
                  강사 배정
                </Button>
              )}
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-[1fr,2fr] gap-4">
                <div className="flex items-center font-semibold">이름:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.student_name}
                  {userType === 'manager' && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'student_name');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">강의:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.lecture || '-'}
                  {/* 학교·학년·강의 연필은 학생 v2 전환 전에만 — 전환 뒤에는 학생 v2 에서만 기록하고 서버가 v1 수정을 409 로 거절한다(결정 18 · ACADEMY_CUTOVER, docs/student-admin-v2/backend/BE-20261014-cutover-flag.md) */}
                  {userType === 'manager' && !ACADEMY_CUTOVER && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'lecture');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">이메일:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.student_email}
                  {userType === 'manager' && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'student_email');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">전화번호:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.mobile_number}
                  {userType === 'manager' && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'mobile_number');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">학부모 전화번호:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.parent_mobile_number}
                  {userType === 'manager' && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'parent_mobile_number');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">학교:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.school_name}
                  {userType === 'manager' && !ACADEMY_CUTOVER && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'school_name');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">학년:</div>
                <div className="group flex items-center">
                  {selectedStudentDetail.school_grade}
                  {userType === 'manager' && !ACADEMY_CUTOVER && (
                    <PenSquare 
                      className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleModalOpen('edit', selectedStudentDetail, 'school_grade');
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center font-semibold">유입 경로:</div>
                <div className="flex items-center">
                  {selectedStudentDetail.acq_channel}
                </div>

                <div className="flex items-center font-semibold">추천인:</div>
                <div className="flex items-center">
                  {selectedStudentDetail.referral_id}
                </div>

                <div className="flex items-center font-semibold">등록일:</div>
                <div>{new Date(selectedStudentDetail.created_at).toLocaleDateString()}</div>
                
                <div className="flex items-center font-semibold">수정일:</div>
                <div>{new Date(selectedStudentDetail.updated_at).toLocaleDateString()}</div>
              </div>
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={showTeacherModal}
        onClose={handleTeacherModalClose}
        title="강사 할당"
        size="sm"
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              variant="danger"
              onClick={() => handleAllocateTeacher(selectedStudent.student_id, null)}
            >
              할당 삭제
            </Button>
            <Button
              variant="secondary"
              onClick={handleTeacherModalClose}
            >
              취소
            </Button>
            <Button
              variant="primary"
              onClick={() => handleAllocateTeacher(selectedStudent.student_id, selectedTeacherId)}
              disabled={!selectedTeacherId}
            >
              할당
            </Button>
          </div>
        }
      >
        <select
          className="w-full rounded border p-2 focus:border-primary focus:ring-primary"
          value={selectedTeacherId}
          onChange={(e) => setSelectedTeacherId(e.target.value)}
        >
          <option value="">강사를 선택하세요</option>
          {allTeachers.map((teacher) => (
            <option key={teacher.user_id} value={teacher.user_id}>
              {teacher.user_name} ({teacher.charge_work})
            </option>
          ))}
        </select>
      </Modal>

      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={async () => {
          try {
            const newPw = await handleResetPassword(selectedStudentDetail.student_id);
            setNewPassword(newPw);
            setShowResetConfirm(false);
            setShowPasswordPopup(true);
          } catch (error) {
            alert(error.message);
          }
        }}
        title="비밀번호 초기화"
        message="정말 비밀번호를 초기화하시겠습니까?"
        confirmText="확인"
        cancelText="취소"
        confirmVariant="danger"
      />

      <Modal
        isOpen={showPasswordPopup}
        onClose={() => setShowPasswordPopup(false)}
        title="새 비밀번호"
        size="sm"
        footer={
          <Button
            variant="secondary"
            onClick={() => setShowPasswordPopup(false)}
          >
            닫기
          </Button>
        }
      >
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newPassword}
            readOnly
            className="flex-1 rounded border bg-gray-100 px-3 py-2"
          />
          <Button
            variant="primary"
            onClick={() => {
              navigator.clipboard.writeText(newPassword);
              alert('비밀번호가 클립보드에 복사되었습니다.');
            }}
          >
            복사
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteEmail('');
          setDeleteError('');
        }}
        title="학생 삭제"
        size="sm"
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteEmail('');
                setDeleteError('');
              }}
            >
              취소
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
            >
              삭제
            </Button>
          </div>
        }
      >
        <p className="mb-4">삭제를 확인하려면 학생의 이메일을 입력하세요:</p>
        <p className="mb-2 text-sm text-gray-600">{selectedStudentDetail?.student_email}</p>
        <input
          type="email"
          value={deleteEmail}
          onChange={(e) => setDeleteEmail(e.target.value)}
          className="mb-4 w-full rounded border px-3 py-2 focus:border-primary focus:ring-primary"
          placeholder="이메일 입력"
        />
        {deleteError && <p className="text-sm text-danger">{deleteError}</p>}
      </Modal>

      <KyTestModal
        key={`kytest-modal-${selectedKyTestStudent?.student_id}-${showKyTestModal}`}
        isOpen={showKyTestModal}
        onClose={() => {
          setShowKyTestModal(false);
          // student를 null로 설정하지 않음 - 다음 열기를 위해 유지
        }}
        student={selectedKyTestStudent}
      />

      {/* 회원 현황 모달 */}
      <MemberStatusModal
        isOpen={showMemberStatus}
        onClose={() => setShowMemberStatus(false)}
      />

      {/* AI 모달 */}
      <Modal
        isOpen={showAIModal && selectedAIStudent}
        onClose={() => {
          setShowAIModal(false);
          setAiTrackerTab('ai'); // 모달 닫을 때 기본 탭으로 리셋
        }}
        title={
          <div className="flex w-full items-center">
            <span className="text-xl font-bold text-gray-800">학습 추적기</span>
            <div className="ml-6 flex items-center gap-1 rounded-full bg-gray-100 p-1">
              <button
                className={`flex items-center gap-1.5 rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                  aiTrackerTab === 'ai'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setAiTrackerTab('ai')}
              >
                <Bot className="h-4 w-4" />
                <span>AI</span>
              </button>
              <button
                className={`flex items-center gap-1.5 rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                  aiTrackerTab === 'engine'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setAiTrackerTab('engine')}
              >
                <Cpu className="h-4 w-4" />
                <span>Engine</span>
              </button>
              <button
                className={`rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                  aiTrackerTab === 'rawdata'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setAiTrackerTab('rawdata')}
              >
                Raw Data
              </button>
            </div>
          </div>
        }
        size="max"
        maxHeight="95vh"
        fixedHeight
        bodyClassName="flex flex-col min-h-0 overflow-hidden"
        footer={
          <Button
            variant="secondary"
            onClick={() => setShowAIModal(false)}
          >
            닫기
          </Button>
        }
      >
        {selectedAIStudent && (
          <AITrackerContent
            studentId={selectedAIStudent.student_id}
            studentInfo={selectedAIStudent}
            activeTab={aiTrackerTab}
            onTabChange={setAiTrackerTab}
          />
        )}
      </Modal>
        </>
      )}
    />
  );
}

export default StudentManagement;